/**
 * serviceWorkerRegistration.js
 *
 * Registers the Workbox-powered service worker built by CRA.
 * The service worker only activates in production (npm run build).
 * Set REACT_APP_SW_DEV=true in .env.local to also activate in development.
 *
 * Usage in index.js:
 *   import * as serviceWorkerRegistration from './serviceWorkerRegistration';
 *   serviceWorkerRegistration.register({ onUpdate, onSuccess });
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// ── Auto-reload when a freshly-deployed SW takes control ─────────────────────
// Pairs with `self.skipWaiting()` + `clientsClaim()` in service-worker.js.
// The moment the new SW activates and claims this page, `controllerchange`
// fires here and we reload so the user lands on the new bundle. Result:
// beta users see updates within seconds of a deploy without ever opening
// DevTools to unregister + clear site data.
//
// One-shot per session — guards against the rare case where another deploy
// fires controllerchange a second time during the same page lifetime
// (which would otherwise reload-loop).
let _reloadingForNewSW = false;
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (_reloadingForNewSW) return;
    _reloadingForNewSW = true;
    window.location.reload();
  });
}

/**
 * @param {{ onUpdate?: (reg: ServiceWorkerRegistration) => void,
 *           onSuccess?: (reg: ServiceWorkerRegistration) => void }} config
 */
export function register(config) {
  const swEnabled =
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_SW_DEV === 'true';

  if (!swEnabled || !('serviceWorker' in navigator)) return;

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    // SW won't work if PUBLIC_URL is on a different origin (e.g. a CDN).
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      // On localhost, validate that the SW file really exists before registering.
      checkValidServiceWorker(swUrl, config);
      navigator.serviceWorker.ready.then(() => {
        console.log('[SW] App is being served cache-first by the service worker.');
      });
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installing = registration.installing;
        if (!installing) return;

        installing.onstatechange = () => {
          if (installing.state !== 'installed') return;

          if (navigator.serviceWorker.controller) {
            // A new SW is waiting — let the caller decide how to notify the user.
            console.log('[SW] New content available; call SKIP_WAITING to activate.');
            config?.onUpdate?.(registration);
          } else {
            // First install — content is now cached for offline use.
            console.log('[SW] Content cached for offline use.');
            config?.onSuccess?.(registration);
          }
        };
      };
    })
    .catch((err) => {
      console.error('[SW] Registration error:', err);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      const notFound =
        response.status === 404 ||
        (contentType != null && !contentType.includes('javascript'));

      if (notFound) {
        // SW not found — unregister and reload.
        navigator.serviceWorker.ready.then((reg) => {
          reg.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[SW] No internet connection — app running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((reg) => reg.unregister())
      .catch((err) => console.error('[SW] Unregister error:', err.message));
  }
}
